<script setup>
// Outbound Marketing — WhatsApp template campaign over a HubSpot segment.
//
// The wizard is strictly sequential because each step's options come from the previous
// answer: templates belong to the agent's WABA, segments belong to the agent's HubSpot
// account, contacts belong to the segment. Picking the agent again resets everything below.
//
// Template choice sits *after* the recipient list, not before. The agent's templates are
// fetched as soon as the agent is picked — but only to answer "can this agent send at all?",
// which is the one thing worth failing on before the operator builds a list. The actual
// pick happens last, against the thumbnails, once it is clear who is receiving it.
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import outboundsAPI from 'dashboard/api/outbounds';
import Button from 'dashboard/components-next/ui/Button.vue';
import StepSection from './components/StepSection.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import AgentPicker from './components/AgentPicker.vue';
import TemplateGallery from './components/TemplateGallery.vue';
import TemplateDetail from './components/TemplateDetail.vue';
import ContactsTable from './components/ContactsTable.vue';
import ContactDialog from './components/ContactDialog.vue';
import DispatchDialog from './components/DispatchDialog.vue';
import DuplicateAlertDialog from './components/DuplicateAlertDialog.vue';
import SegmentSourcePicker from './components/SegmentSourcePicker.vue';
import IntegrationSummary from './components/IntegrationSummary.vue';
import CsvImportCard from './components/CsvImportCard.vue';
import ManualContactsCard from './components/ManualContactsCard.vue';
import HistoryPeople from './components/HistoryPeople.vue';

// Named so the campaigns route view can keep it out of <keep-alive>: this wizard holds a
// reviewed contact list in memory, and a cached instance would resurrect a stale campaign
// — a different agent's contacts under a new template — when the operator navigates away
// and back.
defineOptions({ name: 'OutboundMarketing' });

const { t } = useI18n();
const store = useStore();
const accountId = store.getters.getCurrentAccountId;
const router = useRouter();

// Duas abas: o wizard de disparo e o histórico por destinatário. O histórico
// monta a própria consulta e não compartilha estado com o wizard — trocar de
// aba não pode mexer na lista revisada de contatos.
const activeTab = ref('dispatch');
const tabs = computed(() => [
  {
    key: 'dispatch',
    label: t('OUTBOUNDS.TABS.DISPATCH'),
    icon: 'i-lucide-send',
  },
  {
    key: 'history',
    label: t('OUTBOUNDS.TABS.HISTORY'),
    icon: 'i-lucide-history',
  },
]);

const chatbots = ref([]);
const selectedChatbotId = ref('');
const integration = ref(null);
const templates = ref([]);
// Distinguishes "not fetched yet" from "fetched, and this agent has none" — only the second
// justifies telling the operator the agent cannot send.
const templatesLoaded = ref(false);
const selectedTemplateName = ref('');
const segments = ref([]);
const selectedSegmentId = ref('');
// Rótulos da própria conta — a fonte de audiência que não depende de CRM nenhum.
const labels = ref([]);
const selectedLabelId = ref('');
const contacts = ref([]);

// Where the list came from: a HubSpot segment, an uploaded CSV, or typed by hand.
// Only the first one has a segment behind it; the other two carry their own label.
const contactSource = ref('hubspot');
const localSegmentLabel = ref('');
const dispatchResult = ref(null);

// ── disparo como PLANO ───────────────────────────────────────────────────────────────────
//
// Quando a origem é um segmento do HubSpot e o operador não mexeu na lista, o disparo vira um
// `Campaign`: fica um objeto para trás, e com ele agendamento, janela, reprocesso e relatório
// por evento. A lista é RELIDA no envio — entre escolher e disparar alguém pode ter confirmado,
// e reconvidar quem já respondeu é o erro mais visível que este sistema comete.
//
// Se a lista foi editada, o disparo segue pelo caminho direto: a edição é uma decisão do
// operador ("não mande para esse"), e reler o segmento a jogaria fora em silêncio.
const contactsSnapshot = ref('');
const listaIntacta = computed(
  () =>
    contactsSnapshot.value !== '' &&
    contactsSnapshot.value === assinaturaDaLista()
);
// Toda fonte que o plano sabe reler sozinho pode virar `Campaign`: o segmento do HubSpot e o
// rótulo da conta. CSV e lista manual ficam de fora porque os contatos viajam com o disparo e
// não com o plano — não há o que reler depois.
const fonteDoPlano = computed(() => {
  if (contactSource.value === 'hubspot' && selectedSegmentId.value) {
    return { type: 'hubspot_segment', list_id: selectedSegmentId.value };
  }
  if (contactSource.value === 'label' && selectedLabelId.value) {
    return { type: 'Label', id: selectedLabelId.value };
  }
  return null;
});

function assinaturaDaLista() {
  return contacts.value.map(c => c.contact_id).join(',');
}

// Depois do clique a tela deixa de ser um formulário e vira acompanhamento. Guardar isto é o
// que impede o segundo disparo: o botão não volta a existir, em vez de só ficar desabilitado
// enquanto a requisição corre (o `finally` o reabilitava mesmo quando dava certo).
const disparo = ref(null);
const progresso = ref(null);
let progressoTimer = null;

function pararProgresso() {
  if (progressoTimer) {
    clearTimeout(progressoTimer);
    progressoTimer = null;
  }
}

// O progresso é perguntado com o `display_id`, que é o que a API de campanha usa. Para quando
// conclui, em vez de bater no servidor a cada 3s para sempre.
async function acompanhar() {
  if (!disparo.value?.displayId) return;
  try {
    progresso.value = await outboundsAPI.campaignProgress(
      accountId,
      disparo.value.displayId
    );
  } catch (error) {
    pararProgresso();
    return;
  }
  if (progresso.value?.concluido) {
    pararProgresso();
    return;
  }
  progressoTimer = setTimeout(acompanhar, 3000);
}

const loading = ref({
  chatbots: false,
  integration: false,
  templates: false,
  segments: false,
  contacts: false,
  labels: false,
  sending: false,
});

const editingContact = ref(null);
const contactDialogOpen = ref(false);
const dispatchDialogOpen = ref(false);

// Números desta lista que já receberam um template hoje — em qualquer disparo da
// conta, feito por qualquer operador. Preenchido pela checagem preventiva (aviso na
// tela) e pelo 409 do servidor (que é quem de fato segura o disparo).
const duplicates = ref([]);
const duplicateDialogOpen = ref(false);
// Quem, nesta lista, pediu para não receber disparos. O servidor já os tira de qualquer envio; isto é
// o aviso na confirmação, para ninguém achar que a lista inteira recebeu.
const bloqueados = ref([]);
// Um item por pessoa; `linhas` é quantas linhas desta lista cada uma tira do envio (o mesmo telefone
// pode vir em dois contatos). É o que se desconta de quem recebe.
const linhasBloqueadas = computed(() =>
  bloqueados.value.reduce((total, item) => total + (item.linhas || 1), 0)
);

const selectedChatbot = computed(() =>
  chatbots.value.find(bot => bot.id === selectedChatbotId.value)
);
const selectedTemplate = computed(() =>
  templates.value.find(template => template.name === selectedTemplateName.value)
);
const selectedSegment = computed(() =>
  segments.value.find(segment => segment.id === selectedSegmentId.value)
);
const selectedLabel = computed(() =>
  labels.value.find(label => String(label.id) === String(selectedLabelId.value))
);
const sendableContacts = computed(() =>
  contacts.value.filter(c => c.has_phone)
);

// Todo disparo vira campanha. Antes, mexer na lista fazia o plano ser descartado — e junto
// com ele o registro, o progresso, o detalhe e o reenvio. Revisar os destinatários é a função
// do passo 3: era a própria tela convidando a perder tudo isso.
//
// O que a edição muda é de ONDE a audiência sai, não SE existe campanha. Ver `audienciaDoPlano`.
const podeVirarPlano = computed(() => sendableContacts.value.length > 0);

const hasDuplicates = computed(() => duplicates.value.length > 0);

// Meta only lets you send an approved template; showing the rest just invites a failed send.
//
// The upstream webhook currently asks Meta for `name,components,language` only, so `status`
// arrives empty and this filter passes everything through. It is kept because it is correct
// the moment the field is populated — see OUTBOUNDS.TEMPLATE.DESCRIPTION, which does not
// promise a guarantee the data cannot back.
const sendableTemplates = computed(() =>
  templates.value.filter(
    template => !template.status || template.status === 'APPROVED'
  )
);

const hasTemplates = computed(() => sendableTemplates.value.length > 0);
// Only true once we have actually looked, so the banner never flashes during the fetch.
const agentHasNoTemplates = computed(
  () =>
    Boolean(selectedChatbotId.value) &&
    templatesLoaded.value &&
    !loading.value.templates &&
    !hasTemplates.value
);

const hasHubspot = computed(() =>
  Boolean(integration.value?.has_hubspot_token)
);

// What gets recorded downstream as the campaign's segment.
const segmentLabel = computed(() => {
  if (contactSource.value === 'hubspot')
    return selectedSegment.value?.name || '';
  if (contactSource.value === 'label') return selectedLabel.value?.title || '';
  return localSegmentLabel.value;
});

// O ComboBox fala em {value,label}; rótulo e segmento chegam com formatos próprios.
const opcoesDeRotulo = computed(() =>
  labels.value.map(l => ({ value: String(l.id), label: l.title }))
);
const opcoesDeSegmento = computed(() =>
  segments.value.map(sg => ({ value: String(sg.id), label: sg.name }))
);

// ── trilha de etapas ─────────────────────────────────────────────────────────────────────
//
// Os cinco passos se empilhavam e a página só crescia: a etapa em uso ficava no fim de uma
// rolagem e as anteriores ocupavam espaço sem serem usadas. Justo as duas que mais precisam de
// tela — a tabela de destinatários e a galeria de templates — eram as mais espremidas.
//
// Agora aparece uma por vez, com a trilha no topo dizendo onde se está. As condições abaixo são
// as MESMAS que antes faziam cada cartão aparecer; o que mudou é onde elas são lidas.
const etapaAtual = ref(0);

const etapas = computed(() => [
  {
    titulo: t('OUTBOUNDS.STEPS.AGENT'),
    ok: Boolean(selectedChatbotId.value) && hasTemplates.value,
  },
  { titulo: t('OUTBOUNDS.STEPS.SOURCE'), ok: contacts.value.length > 0 },
  {
    titulo: t('OUTBOUNDS.STEPS.CONTACTS'),
    ok: sendableContacts.value.length > 0,
  },
  {
    titulo: t('OUTBOUNDS.STEPS.TEMPLATE'),
    ok: Boolean(selectedTemplateName.value),
  },
  { titulo: t('OUTBOUNDS.STEPS.SEND'), ok: false },
]);

// Até onde dá para navegar: a primeira não respondida. Voltar é livre; pular não.
const ultimaLiberada = computed(() => {
  const i = etapas.value.findIndex(e => !e.ok);
  return i === -1 ? etapas.value.length - 1 : i;
});

function irParaEtapa(i) {
  if (i <= ultimaLiberada.value) etapaAtual.value = i;
}

// Avanço automático, como era antes: responder uma etapa leva à seguinte, sem pedir "continuar".
// Só na TRANSIÇÃO de não-respondida para respondida, e só se for a etapa em que se está — senão
// voltar para conferir uma escolha antiga empurraria a pessoa para frente de novo.
watch(
  () => etapas.value.map(e => e.ok),
  (agora, antes) => {
    if (!antes) return;
    const i = etapaAtual.value;
    if (agora[i] && !antes[i] && i < etapas.value.length - 1)
      etapaAtual.value = i + 1;
  }
);

// O que cada passo respondeu, em uma linha. Depois de disparar, o wizard inteiro vira isto:
// o formulário já não serve a nada, mas o contexto do que saiu ainda importa.
const resumoDosPassos = computed(() => [
  {
    rotulo: t('OUTBOUNDS.AGENT.TITLE'),
    valor: selectedChatbot.value?.name || '',
  },
  { rotulo: t('OUTBOUNDS.SOURCE.TITLE'), valor: segmentLabel.value || '' },
  {
    rotulo: t('OUTBOUNDS.CONTACTS.TITLE'),
    valor: (() => {
      const n = disparo.value?.total ?? sendableContacts.value.length;
      return t('OUTBOUNDS.DISPATCH.RECIPIENTS_N', { count: n }, n);
    })(),
  },
  { rotulo: t('OUTBOUNDS.TEMPLATE.TITLE'), valor: selectedTemplateName.value },
]);

const readyToDispatch = computed(
  () => Boolean(selectedTemplateName.value) && sendableContacts.value.length > 0
);

// ── nome e hora ──────────────────────────────────────────────────────────────────────────
//
// O operador nunca nomeava a campanha: o backend usava o nome da audiência como título, e por
// isso a lista tinha cinco campanhas "Prova da Fase 2" indistinguíveis. Sugerido, não
// obrigatório — quem só quer disparar não é obrigado a inventar um nome.
const nomeDaCampanha = ref('');
const nomeSugerido = computed(() => {
  const quando = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
  const base = segmentLabel.value || selectedTemplateName.value;
  return base ? `${base} · ${quando}` : '';
});
const tituloParaEnviar = computed(
  () => nomeDaCampanha.value.trim() || nomeSugerido.value
);

// Preenche a sugestão quando o passo aparece, sem sobrescrever o que o operador digitou.
watch(readyToDispatch, pronto => {
  if (pronto && !nomeDaCampanha.value)
    nomeDaCampanha.value = nomeSugerido.value;
});

const MODO_AGORA = 'agora';
const MODO_AGENDAR = 'agendar';
const modoDeEnvio = ref(MODO_AGORA);
const dataAgendada = ref('');

// `datetime-local` entrega hora LOCAL sem fuso ("2026-09-23T09:00"). Mandar essa string crua faz
// o servidor lê-la como UTC e a campanha sai três horas fora. `new Date()` a interpreta no fuso
// do navegador, e o ISO resultante carrega o deslocamento certo.
const agendamentoISO = computed(() => {
  if (modoDeEnvio.value !== MODO_AGENDAR || !dataAgendada.value) return null;
  const momento = new Date(dataAgendada.value);
  return Number.isNaN(momento.getTime()) ? null : momento.toISOString();
});

// O mínimo do campo é agora: o backend recusa data no passado, e é melhor o operador não
// conseguir escolhê-la do que levar 422 depois de preencher o wizard inteiro.
const agoraLocal = computed(() => {
  const agora = new Date();
  return new Date(agora.getTime() - agora.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
});

const agendamentoInvalido = computed(
  () => modoDeEnvio.value === MODO_AGENDAR && !agendamentoISO.value
);

// O passo 5 afirmava "sai na hora e nao tem como cancelar" sempre. Com agendamento isso passou a
// contradizer o proprio radio logo abaixo, que promete o contrario.
const descricaoDoEnvio = computed(() =>
  modoDeEnvio.value === MODO_AGENDAR
    ? t('OUTBOUNDS.DISPATCH.DESCRIPTION_SCHEDULED')
    : t('OUTBOUNDS.DISPATCH.DESCRIPTION_NOW')
);

// The banner reports how far down the chain the last successful fetch got, so the operator
// can tell "agent connected" from "agent connected and its segments are in" without
// scrolling back up through the wizard.
const connectionStatus = computed(() => {
  if (contacts.value.length) {
    return t('OUTBOUNDS.STATUS.CONTACTS_LOADED', {
      count: contacts.value.length,
    });
  }
  if (segments.value.length) {
    return t('OUTBOUNDS.STATUS.SEGMENTS_LOADED');
  }
  if (hasTemplates.value) {
    return t('OUTBOUNDS.STATUS.TEMPLATES_LOADED', {
      count: sendableTemplates.value.length,
    });
  }
  return '';
});

const templateDetailRef = ref(null);

// Picking a thumbnail opens the expanded preview above the grid, which — on a long list —
// renders off-screen above the fold the operator is looking at. Scrolling them to it is
// the difference between "nothing happened" and "here is what you just chose".
watch(selectedTemplateName, async name => {
  if (!name) return;
  await nextTick();
  templateDetailRef.value?.$el?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
});

// Clearing the agent is enough — its watcher already invalidates every downstream answer.
function switchAgent() {
  selectedChatbotId.value = '';
}

// CSV and manual produce contacts directly; the parent owns the reviewed list so that
// editing and removing a row keeps working the same way for every origin.
function applyLocalContacts({ contacts: list, label }) {
  contacts.value = list;
  localSegmentLabel.value = label;
}

// The controller answers { error: "<mensagem>" } with a message written for the operator —
// prefer it over the generic fallback, which only covers network-level failures.
function errorMessage(error, fallback) {
  return error?.response?.data?.error || fallback;
}

// Planilha sobe para o backend Go, que a lê e devolve as linhas. O rascunho criado aqui serve
// só para transportar o arquivo: no disparo a lista é congelada de novo, a partir da tabela.
async function importarPlanilha({ file, label }) {
  loading.value.contacts = true;
  try {
    const { id } = await outboundsAPI.draftCreate(accountId, {
      chatbotId: selectedChatbotId.value,
      source: 'spreadsheet',
      segmentLabel: label,
    });
    await outboundsAPI.draftImport(accountId, id, file);
    contacts.value = (await outboundsAPI.draftContacts(accountId, id)) || [];
    localSegmentLabel.value = label;
    contactsSnapshot.value = assinaturaDaLista();
  } catch (error) {
    contacts.value = [];
    useAlert(errorMessage(error, t('OUTBOUNDS.SOURCE.CSV_ERROR.EMPTY')));
  } finally {
    loading.value.contacts = false;
  }
}

async function fetchChatbots() {
  loading.value.chatbots = true;
  try {
    chatbots.value = (await outboundsAPI.chatbots(accountId)) || [];
  } catch (error) {
    useAlert(errorMessage(error, t('OUTBOUNDS.AGENT.ERROR')));
  } finally {
    loading.value.chatbots = false;
  }
}

async function fetchTemplates() {
  loading.value.templates = true;
  try {
    templates.value =
      (await outboundsAPI.templates(accountId, selectedChatbotId.value)) || [];
    if (!templates.value.length) {
      useAlert(t('OUTBOUNDS.TEMPLATE.EMPTY_ALERT'));
    }
  } catch (error) {
    templates.value = [];
    useAlert(errorMessage(error, t('OUTBOUNDS.TEMPLATE.ERROR')));
  } finally {
    templatesLoaded.value = true;
    loading.value.templates = false;
  }
}

async function fetchSegments() {
  loading.value.segments = true;
  try {
    segments.value =
      (await outboundsAPI.segments(accountId, selectedChatbotId.value)) || [];
  } catch (error) {
    segments.value = [];
    useAlert(errorMessage(error, t('OUTBOUNDS.SEGMENT.ERROR')));
  } finally {
    loading.value.segments = false;
  }
}

async function fetchLabels() {
  loading.value.labels = true;
  try {
    labels.value = (await outboundsAPI.labels(accountId)) || [];
  } catch (error) {
    labels.value = [];
    useAlert(errorMessage(error, t('OUTBOUNDS.LABEL.ERROR')));
  } finally {
    loading.value.labels = false;
  }
}

async function fetchContacts() {
  loading.value.contacts = true;
  try {
    contacts.value =
      (await outboundsAPI.contacts(accountId, selectedChatbotId.value, {
        listId:
          contactSource.value === 'hubspot'
            ? selectedSegmentId.value
            : undefined,
        labelId:
          contactSource.value === 'label' ? selectedLabelId.value : undefined,
      })) || [];
    // A assinatura do que o servidor devolveu. É comparando com ela que se sabe se o operador
    // mexeu na lista — e, se mexeu, o disparo não pode reler o segmento por cima da decisão.
    contactsSnapshot.value = assinaturaDaLista();
  } catch (error) {
    contacts.value = [];
    contactsSnapshot.value = '';
    useAlert(errorMessage(error, t('OUTBOUNDS.CONTACTS.ERROR')));
  } finally {
    loading.value.contacts = false;
  }
}

// Agent change invalidates every downstream answer.
watch(selectedChatbotId, async id => {
  integration.value = null;
  templates.value = [];
  templatesLoaded.value = false;
  selectedTemplateName.value = '';
  segments.value = [];
  selectedSegmentId.value = '';
  selectedLabelId.value = '';
  contacts.value = [];
  localSegmentLabel.value = '';
  dispatchResult.value = null;
  duplicates.value = [];
  if (!id) return;

  loading.value.integration = true;
  try {
    integration.value = await outboundsAPI.integration(accountId, id);
  } catch (error) {
    useAlert(errorMessage(error, t('OUTBOUNDS.AGENT.NO_INTEGRATION')));
    return;
  } finally {
    loading.value.integration = false;
  }

  // An agent without a HubSpot token can still send to a spreadsheet or a typed list —
  // the dispatch endpoint never required HubSpot.
  contactSource.value = hasHubspot.value ? 'hubspot' : 'csv';

  await fetchTemplates();
  // No approved template means no campaign at all; loading segments would only offer the
  // operator a list they cannot send to.
  if (hasHubspot.value && hasTemplates.value) await fetchSegments();
});

watch(selectedSegmentId, id => {
  contacts.value = [];
  if (id) fetchContacts();
});

watch(selectedLabelId, id => {
  contacts.value = [];
  if (id) fetchContacts();
});

// Switching origin throws away the list built with the previous one; keeping it would
// dispatch a spreadsheet under a HubSpot segment's name.
watch(contactSource, origem => {
  contacts.value = [];
  localSegmentLabel.value = '';
  selectedSegmentId.value = '';
  selectedLabelId.value = '';
  dispatchResult.value = null;
  duplicates.value = [];
  // Os rótulos só são buscados quando alguém escolhe a origem: é uma conta inteira de
  // rótulos, e a maioria dos disparos nunca passa por aqui.
  if (origem === 'label' && !labels.value.length) fetchLabels();
});

function openContactEditor(contact) {
  editingContact.value = contact;
  contactDialogOpen.value = true;
}

function saveContact(updated) {
  const phone = String(updated.phone || '').replace(/[^0-9+]/g, '');
  const index = contacts.value.findIndex(
    c => c.contact_id === updated.contact_id
  );
  if (index === -1) return;

  contacts.value.splice(index, 1, {
    ...updated,
    phone,
    has_phone: phone.length >= 8,
  });
}

function removeContact(contact) {
  contacts.value = contacts.value.filter(
    c => c.contact_id !== contact.contact_id
  );
}

// A checagem só precisa de telefone e nome: mandar a lista inteira a cada mudança
// da tabela seria trafegar o segmento todo para responder uma pergunta sobre números.
function checkPayload() {
  return sendableContacts.value.map(contact => ({
    contact_id: contact.contact_id,
    first_name: contact.first_name,
    last_name: contact.last_name,
    phone: contact.phone,
  }));
}

// Aviso preventivo: roda quando a lista muda, antes de o operador escolher template.
// Falha em silêncio de propósito — o gate real é o 409 do servidor no disparo, e um
// erro de rede aqui não pode virar um toast em cima de quem só montou uma lista.
async function refreshDuplicates() {
  if (!sendableContacts.value.length) {
    duplicates.value = [];
    bloqueados.value = [];
    return;
  }

  try {
    const result = await outboundsAPI.dispatchCheck(accountId, {
      contacts: checkPayload(),
    });
    duplicates.value = result?.duplicates || [];
    bloqueados.value = result?.bloqueados || [];
  } catch {
    duplicates.value = [];
    bloqueados.value = [];
  }
}

// Tira da lista só quem já recebeu hoje e devolve o operador para a confirmação com
// o lote menor. O resto do segmento continua valendo — cancelar tudo por causa de
// três repetidos é o que fazia a pessoa disparar de novo mais tarde.
function removeDuplicates() {
  // `filter(Boolean)`: um contato sem id colocaria `undefined` no Set, e aí toda
  // linha sem id casaria — sairiam da lista contatos que ninguém marcou como
  // repetidos, sem nenhum aviso.
  const repeated = new Set(
    duplicates.value.map(item => item.contact_id).filter(Boolean)
  );
  const repeatedPhones = new Set(
    duplicates.value.map(item => item.phone).filter(Boolean)
  );

  contacts.value = contacts.value.filter(
    contact =>
      !repeated.has(contact.contact_id) && !repeatedPhones.has(contact.phone)
  );
  duplicates.value = [];
  duplicateDialogOpen.value = false;

  if (!sendableContacts.value.length) {
    useAlert(t('OUTBOUNDS.DUPLICATES.NOTHING_LEFT'));
    return;
  }

  dispatchDialogOpen.value = true;
}

async function dispatch({ force = false } = {}) {
  // `force` responde ao aviso de duplicata — não escolhe o caminho do disparo. Enquanto ele
  // escolhia, mandar de novo para quem já recebeu hoje jogava fora a campanha: sem registro na
  // lista, sem progresso, sem detalhe, sem reenvio. Justo no caso em que quem opera mais precisa
  // de rastro. O porteiro de duplicata vive no `dispatch_campaign`; o plano não o tem, e por
  // isso aqui o `force` simplesmente não se aplica.
  if (podeVirarPlano.value) {
    await dispararComoPlano();
    return;
  }
  loading.value.sending = true;
  try {
    const result = await outboundsAPI.dispatch(accountId, {
      chatbotId: selectedChatbotId.value,
      chatbotName: selectedChatbot.value?.name || '',
      templateName: selectedTemplateName.value,
      segment: segmentLabel.value,
      contacts: sendableContacts.value,
      acknowledgeDuplicates: force,
    });
    dispatchDialogOpen.value = false;
    duplicateDialogOpen.value = false;
    // A lista acabou de virar histórico: manter o aviso antigo diria "já disparado
    // hoje" sobre o disparo que o próprio operador acabou de fazer.
    duplicates.value = [];

    // `sent_count` is how many contacts were handed to BPM, not how many Meta accepted —
    // the webhook answers before the sends happen. Report enqueueing, and when the count
    // is missing say so instead of echoing back the number we just sent.
    const queued = result?.sent_count;
    dispatchResult.value = { count: queued ?? null };
    // Este caminho não deixa campanha para trás, então não há progresso para acompanhar — mas
    // também não pode deixar a pessoa diante de um formulário que já disparou. Marca o estado
    // para a tela trocar de função e o botão sumir.
    disparo.value = {
      displayId: null,
      agendada: false,
      total: sendableContacts.value.length,
    };
    useAlert(
      queued == null
        ? t('OUTBOUNDS.DISPATCH.QUEUED_UNKNOWN')
        : t('OUTBOUNDS.DISPATCH.QUEUED', { count: queued })
    );
  } catch (error) {
    // 409 = o servidor recusou porque a lista repete disparo de hoje. Não é erro de
    // rede nem de template: é a resposta que carrega quais números repetiram.
    if (error?.response?.status === 409) {
      duplicates.value = error.response.data?.duplicates || [];
      dispatchDialogOpen.value = false;
      duplicateDialogOpen.value = true;
      return;
    }
    useAlert(errorMessage(error, t('OUTBOUNDS.DISPATCH.ERROR')));
  } finally {
    loading.value.sending = false;
  }
}

// Congela a lista revisada num rascunho do Go e devolve a audiência que aponta para ele.
// Sempre uma lista NOVA: se o operador removeu alguém da tabela depois de importar, é a
// tabela que vale — reaproveitar o rascunho da importação mandaria para quem ele tirou.
async function audienciaDoPlano() {
  // A fonte original só pode ser RELIDA no disparo quando a lista está intacta: entre escolher e
  // disparar alguém pode ter entrado no rótulo ou confirmado no segmento, e reler é o que mantém
  // a audiência viva. Se o operador mexeu, reler jogaria a decisão dele fora em silêncio.
  if (fonteDoPlano.value && listaIntacta.value) return fonteDoPlano.value;

  // Lista revisada — de rótulo, de segmento, de planilha ou digitada — é congelada num rascunho.
  // A regra é a mesma de sempre: a tabela revisada É a audiência.
  const { id } = await outboundsAPI.draftCreate(accountId, {
    chatbotId: selectedChatbotId.value,
    source: contactSource.value === 'manual' ? 'manual' : 'spreadsheet',
    segmentLabel: segmentLabel.value,
  });
  await outboundsAPI.draftAddContacts(accountId, id, sendableContacts.value);
  return { type: 'outbound_campaign', id };
}

// ── prévia do template ───────────────────────────────────────────────────────────────────
//
// A galeria mostrava `Oi, {{1}}!` e ninguém sabia o que sairia ali. A prévia é PEDIDA ao backend
// Go, que é quem resolve as variáveis — replicar a convenção aqui seria uma segunda cópia da
// regra. Ela traz de quebra dois avisos que o OS vinha jogando fora: variável que fica VAZIA
// (o "Olá ," que só aparece depois de enviado) e template de marketing, que a Meta pode não
// entregar a quem estourou o limite mensal. Foi esse o caso do disparo que não chegou.
const previa = ref(null);
const previaErro = ref('');

async function carregarPrevia() {
  previa.value = null;
  previaErro.value = '';
  if (!selectedTemplateName.value || !sendableContacts.value.length) return;

  const amostra = sendableContacts.value[0];
  try {
    previa.value = await outboundsAPI.templatePreview(accountId, {
      chatbotId: selectedChatbotId.value,
      templateName: selectedTemplateName.value,
      language: selectedTemplate.value?.language || '',
      contact: {
        first_name: amostra.first_name || '',
        last_name: amostra.last_name || '',
        email: amostra.email || '',
        phone: amostra.phone || '',
        company: amostra.company || '',
      },
    });
  } catch (error) {
    // Prévia é conforto, não pré-requisito: quem não conseguir vê-la ainda dispara.
    previaErro.value = errorMessage(error, t('OUTBOUNDS.PREVIEW.ERROR'));
  }
}

watch([selectedTemplateName, sendableContacts], carregarPrevia);

// Montado aqui porque chave dupla dentro de interpolação quebra o parser do template.
function marcador(nome) {
  return `{{${nome}}}`;
}

onBeforeUnmount(pararProgresso);

function verDetalhe() {
  router.push({
    name: 'campaigns_detail',
    params: { accountId: accountId, campaignId: disparo.value.displayId },
  });
}

// Montar outra do zero. Limpa o que identifica a campanha anterior e mantém o agente, que é a
// escolha que raramente muda entre um disparo e o seguinte.
function recomecar() {
  pararProgresso();
  disparo.value = null;
  progresso.value = null;
  dispatchResult.value = null;
  nomeDaCampanha.value = '';
  modoDeEnvio.value = MODO_AGORA;
  dataAgendada.value = '';
  contacts.value = [];
  contactsSnapshot.value = '';
  selectedTemplateName.value = '';
  selectedLabelId.value = '';
  selectedSegmentId.value = '';
  localSegmentLabel.value = '';
}

function irParaAsCampanhas() {
  router.push({ name: 'campaigns_index', params: { accountId: accountId } });
}

async function dispararComoPlano() {
  loading.value.sending = true;
  try {
    const result = await outboundsAPI.dispatchAsCampaign(accountId, {
      chatbotId: selectedChatbotId.value,
      templateName: selectedTemplateName.value,
      segment: segmentLabel.value,
      audience: [await audienciaDoPlano()],
      title: tituloParaEnviar.value,
      scheduledAt: agendamentoISO.value,
    });
    dispatchDialogOpen.value = false;
    duplicateDialogOpen.value = false;
    duplicates.value = [];
    // A tela troca de função aqui: o formulário sai, o acompanhamento entra. Guardar isto é
    // também o que impede o segundo disparo — o botão deixa de existir.
    disparo.value = {
      displayId: result?.display_id || null,
      agendada: Boolean(agendamentoISO.value),
      total: sendableContacts.value.length,
    };

    // Campanha agendada não tem progresso para acompanhar: o lote só nasce quando o cron a
    // colhe. Ligar a barra aqui deixaria um "0 de 0" girando até alguém sair da tela.
    if (agendamentoISO.value) {
      useAlert(t('OUTBOUNDS.DISPATCH.PLAN_SCHEDULED'));
      return;
    }

    useAlert(t('OUTBOUNDS.DISPATCH.PLAN_QUEUED'));
    acompanhar();
  } catch (error) {
    useAlert(errorMessage(error, t('OUTBOUNDS.DISPATCH.ERROR')));
  } finally {
    loading.value.sending = false;
  }
}

// Nova lista, nova checagem. `deep` porque editar ou remover uma linha muda o array
// no lugar, e é exatamente aí que um número novo pode entrar.
watch(contacts, refreshDuplicates, { deep: true });

onMounted(fetchChatbots);
</script>

<template>
  <!-- The page carries its own container because it is mounted straight into the
       campaigns route view, which — unlike SettingsWrapper and CampaignLayout — hands its
       child the bare viewport. Same `max-w-5xl` the sibling campaign pages read at, so the
       wizard lines up with Live chat and SMS instead of running edge to edge. -->
  <div class="flex flex-col w-full max-w-5xl gap-4 px-6 py-4 mx-auto">
    <header>
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('OUTBOUNDS.TITLE') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-10">{{ t('OUTBOUNDS.SUBTITLE') }}</p>
    </header>

    <div class="border-b border-n-weak">
      <div class="flex gap-6">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="flex items-center gap-2 pb-2 text-sm transition-colors border-b-2"
          :class="
            activeTab === tab.key
              ? 'border-n-brand text-n-brand'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = tab.key"
        >
          <i :class="tab.icon" class="size-4" />
          {{ tab.label }}
        </button>
      </div>
    </div>

    <div v-if="activeTab === 'dispatch'" class="flex flex-col gap-4">
      <div
        v-if="integration"
        class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border rounded-xl border-n-teal-6 bg-n-teal-2"
      >
        <span
          class="flex items-center gap-2 text-sm font-medium text-n-teal-11"
        >
          <span class="rounded-full size-2 bg-n-teal-9" />
          {{ t('OUTBOUNDS.STATUS.CONNECTED') }}
        </span>
        <span v-if="connectionStatus" class="text-xs text-n-slate-11">
          {{ connectionStatus }}
        </span>
        <Button variant="outline" size="sm" @click="switchAgent">
          {{ t('OUTBOUNDS.STATUS.SWITCH') }}
        </Button>
      </div>

      <!-- A trilha: onde se está, e o caminho de volta. Concluída volta com um clique; a que
           ainda não tem resposta fica apagada, porque cada etapa só existe depois da anterior. -->
      <nav
        v-if="!disparo"
        class="flex items-center gap-1 overflow-x-auto pb-1"
        :aria-label="t('OUTBOUNDS.STEPS.NAV')"
      >
        <template v-for="(etapa, i) in etapas" :key="etapa.titulo">
          <button
            type="button"
            :disabled="i > ultimaLiberada"
            :aria-current="i === etapaAtual ? 'step' : undefined"
            class="flex items-center gap-2 px-3 py-1.5 text-sm rounded-full whitespace-nowrap border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
            :class="
              i === etapaAtual
                ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                : 'border-transparent text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="irParaEtapa(i)"
          >
            <span
              class="grid rounded-full size-5 place-items-center text-[0.65rem] font-semibold"
              :class="
                etapa.ok && i !== etapaAtual
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : i === etapaAtual
                    ? 'bg-n-brand text-white'
                    : 'bg-n-alpha-2 text-n-slate-10'
              "
            >
              <i
                v-if="etapa.ok && i !== etapaAtual"
                class="i-lucide-check size-3"
              />
              <template v-else>{{ i + 1 }}</template>
            </span>
            {{ etapa.titulo }}
          </button>
          <i
            v-if="i < etapas.length - 1"
            class="i-lucide-chevron-right size-4 shrink-0 text-n-slate-9"
          />
        </template>
      </nav>

      <!-- Depois de disparar, os quatro primeiros passos viram uma linha cada: o formulário já
           cumpriu a função, mas saber o que saiu continua importando. -->
      <div
        v-if="disparo"
        class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <div
          v-for="item in resumoDosPassos"
          :key="item.rotulo"
          class="flex items-baseline gap-2 text-xs"
        >
          <span class="w-32 shrink-0 text-n-slate-10">{{ item.rotulo }}</span>
          <span class="font-medium truncate text-n-slate-12">
            {{ item.valor || '—' }}
          </span>
        </div>
      </div>

      <StepSection
        v-if="!disparo && etapaAtual === 0"
        :step="1"
        :title="t('OUTBOUNDS.AGENT.TITLE')"
        :description="t('OUTBOUNDS.AGENT.DESCRIPTION')"
        :done="Boolean(selectedChatbotId) && hasTemplates"
      >
        <AgentPicker
          v-model="selectedChatbotId"
          :chatbots="chatbots"
          :loading="loading.chatbots"
        />

        <IntegrationSummary
          :integration="integration"
          :loading="loading.integration"
        />

        <p v-if="loading.templates" class="mt-3 text-xs text-n-slate-10">
          {{ t('OUTBOUNDS.TEMPLATE.CHECKING') }}
        </p>

        <!-- The whole flow dead-ends here, so it is a blocking banner and not a toast the
           operator can scroll past. -->
        <div
          v-else-if="agentHasNoTemplates"
          class="flex items-start gap-2 px-4 py-3 mt-3 border rounded-xl border-n-ruby-6 bg-n-ruby-2"
        >
          <i
            class="i-lucide-alert-triangle size-4 mt-0.5 shrink-0 text-n-ruby-11"
          />
          <div>
            <p class="text-xs font-semibold text-n-ruby-11">
              {{ t('OUTBOUNDS.TEMPLATE.NONE_TITLE') }}
            </p>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ t('OUTBOUNDS.TEMPLATE.NONE_BODY') }}
            </p>
          </div>
        </div>

        <p
          v-else-if="selectedChatbotId && hasTemplates"
          class="flex items-center gap-2 mt-3 text-xs text-n-teal-11"
        >
          <i class="i-lucide-check-circle-2 size-4" />
          {{
            t('OUTBOUNDS.TEMPLATE.AVAILABLE', {
              count: sendableTemplates.length,
            })
          }}
        </p>
      </StepSection>

      <StepSection
        v-if="!disparo && etapaAtual === 1"
        :step="2"
        :title="t('OUTBOUNDS.SOURCE.TITLE')"
        :description="t('OUTBOUNDS.SOURCE.DESCRIPTION')"
        :done="Boolean(contacts.length)"
      >
        <SegmentSourcePicker
          v-model="contactSource"
          :hubspot-available="hasHubspot"
        />

        <template v-if="contactSource === 'hubspot'">
          <div
            v-if="!hasHubspot"
            class="px-3 py-2 text-sm rounded-lg bg-n-amber-2 text-n-amber-11"
          >
            {{ t('OUTBOUNDS.SEGMENT.NO_HUBSPOT') }}
          </div>

          <template v-else>
            <div class="flex items-center gap-2 mb-3 text-xs text-n-teal-11">
              <i class="i-lucide-plug-zap size-4" />
              {{ t('OUTBOUNDS.SEGMENT.CONNECTED') }}
            </div>

            <p v-if="loading.segments" class="text-sm text-n-slate-10">
              {{ t('OUTBOUNDS.SEGMENT.LOADING') }}
            </p>
            <ComboBox
              v-else
              v-model="selectedSegmentId"
              :options="opcoesDeSegmento"
              :placeholder="t('OUTBOUNDS.SEGMENT.PLACEHOLDER')"
              :search-placeholder="t('OUTBOUNDS.SEGMENT.SEARCH')"
              :empty-state="t('OUTBOUNDS.SEGMENT.EMPTY')"
              class="w-full max-w-md"
            />

            <p
              v-if="!loading.segments && segments.length"
              class="mt-2 text-xs text-n-slate-10"
            >
              {{ t('OUTBOUNDS.SEGMENT.FOUND', { count: segments.length }) }}
            </p>
          </template>
        </template>

        <template v-else-if="contactSource === 'label'">
          <p v-if="loading.labels" class="text-sm text-n-slate-10">
            {{ t('OUTBOUNDS.LABEL.LOADING') }}
          </p>
          <div
            v-else-if="!labels.length"
            class="px-3 py-2 text-sm rounded-lg bg-n-amber-2 text-n-amber-11"
          >
            {{ t('OUTBOUNDS.LABEL.EMPTY') }}
          </div>
          <template v-else>
            <!-- ComboBox e não `<select>`: uma conta com dezenas de rótulos vira uma lista
                 que só se percorre rolando, e achar o certo exige saber a ordem. O ComboBox
                 filtra enquanto se digita. -->
            <ComboBox
              v-model="selectedLabelId"
              :options="opcoesDeRotulo"
              :placeholder="t('OUTBOUNDS.LABEL.PLACEHOLDER')"
              :search-placeholder="t('OUTBOUNDS.LABEL.SEARCH')"
              :empty-state="t('OUTBOUNDS.LABEL.EMPTY')"
              class="w-full max-w-md"
            />
            <p class="mt-2 text-xs text-n-slate-10">
              {{ t('OUTBOUNDS.LABEL.FOUND', { count: labels.length }) }}
            </p>
          </template>
        </template>

        <!-- Remounting on agent change clears the picked file / typed rows, which would
           otherwise survive into a campaign for a different number. -->
        <CsvImportCard
          v-else-if="contactSource === 'csv'"
          :key="`csv-${selectedChatbotId}`"
          @loaded="applyLocalContacts"
          @upload="importarPlanilha"
        />

        <ManualContactsCard
          v-else
          :key="`manual-${selectedChatbotId}`"
          @loaded="applyLocalContacts"
        />
      </StepSection>

      <StepSection
        v-if="!disparo && etapaAtual === 2"
        :step="3"
        :title="t('OUTBOUNDS.CONTACTS.TITLE')"
        :description="t('OUTBOUNDS.CONTACTS.DESCRIPTION')"
        :done="Boolean(sendableContacts.length)"
      >
        <ContactsTable
          :contacts="contacts"
          :loading="loading.contacts"
          @edit="openContactEditor"
          @remove="removeContact"
        />
      </StepSection>

      <StepSection
        v-if="!disparo && etapaAtual === 3"
        :step="4"
        :title="t('OUTBOUNDS.TEMPLATE.TITLE')"
        :description="t('OUTBOUNDS.TEMPLATE.DESCRIPTION')"
        :done="Boolean(selectedTemplateName)"
      >
        <!-- The chosen template expands above the grid and pushes it down, instead of
           living in a narrow side column. The artwork is the thing being judged, and a
           320px rail was never enough to judge it. -->
        <TemplateDetail
          v-if="selectedTemplate"
          ref="templateDetailRef"
          :template="selectedTemplate"
          :agent-name="selectedChatbot?.name || ''"
          :agent-avatar="selectedChatbot?.url_image || ''"
          class="mb-5"
          @clear="selectedTemplateName = ''"
        />

        <TemplateGallery
          v-model="selectedTemplateName"
          :templates="sendableTemplates"
          :loading="loading.templates"
        />
      </StepSection>

      <!-- Acompanhamento: o passo 5 troca de conteúdo em vez de a pessoa trocar de tela. Os
           números vêm de `campaign_dispatches`, que conta o que de fato saiu. -->
      <StepSection
        v-if="disparo"
        :step="5"
        :title="
          disparo.agendada
            ? t('OUTBOUNDS.DISPATCH.SCHEDULED_TITLE')
            : t('OUTBOUNDS.DISPATCH.SENDING_TITLE')
        "
        :description="
          disparo.agendada
            ? t('OUTBOUNDS.DISPATCH.SCHEDULED_HINT')
            : t('OUTBOUNDS.DISPATCH.SENDING_HINT')
        "
        :done="Boolean(progresso?.concluido) || disparo.agendada"
      >
        <div v-if="progresso && !disparo.agendada" class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-12">
              {{
                progresso.concluido
                  ? t('OUTBOUNDS.DISPATCH.HANDED_OVER')
                  : t('OUTBOUNDS.DISPATCH.SENDING_TITLE')
              }}
            </span>
            <span class="text-xs tabular-nums text-n-slate-11">
              {{ progresso.enviados }} / {{ progresso.total || disparo.total }}
            </span>
          </div>
          <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
            <div
              class="h-full transition-all duration-500 rounded-full bg-n-brand"
              :style="{
                width: progresso.total
                  ? `${Math.round((progresso.enviados / progresso.total) * 100)}%`
                  : '0%',
              }"
            />
          </div>
          <p v-if="progresso.erro" class="text-xs text-n-ruby-11">
            {{ progresso.erro }}
          </p>
        </div>

        <div class="flex flex-wrap gap-2 mt-4">
          <Button v-if="disparo.displayId" size="sm" @click="verDetalhe">
            {{ t('OUTBOUNDS.DISPATCH.SEE_DETAIL') }}
          </Button>
          <Button variant="outline" size="sm" @click="irParaAsCampanhas">
            {{ t('OUTBOUNDS.DISPATCH.BACK_TO_LIST') }}
          </Button>
          <Button variant="outline" size="sm" @click="recomecar">
            {{ t('OUTBOUNDS.DISPATCH.NEW_ONE') }}
          </Button>
        </div>
      </StepSection>

      <StepSection
        v-if="!disparo && etapaAtual === 4"
        :step="5"
        :title="t('OUTBOUNDS.DISPATCH.TITLE')"
        :description="descricaoDoEnvio"
        :done="Boolean(dispatchResult)"
      >
        <!-- Aviso preventivo: aparece assim que a lista fica pronta, antes de o operador
             chegar ao botão. Nomeia os números porque é o número que identifica o
             cliente dos dois lados — quem disparou de manhã e quem está disparando
             agora. O bloqueio de verdade continua sendo o do servidor. -->
        <div
          v-if="hasDuplicates"
          class="flex items-start gap-2 px-4 py-3 mb-3 border rounded-xl border-n-amber-6 bg-n-amber-2"
        >
          <i
            class="i-lucide-alert-triangle size-4 mt-0.5 shrink-0 text-n-amber-11"
          />
          <div class="min-w-0">
            <p class="text-xs font-semibold text-n-amber-11">
              {{
                t('OUTBOUNDS.DUPLICATES.BANNER_TITLE', {
                  count: duplicates.length,
                })
              }}
            </p>
            <p class="mt-1 text-xs break-all text-n-slate-11">
              {{ duplicates.map(item => item.phone).join(' · ') }}
            </p>
            <button
              type="button"
              class="mt-1 text-xs font-medium underline text-n-amber-11"
              @click="duplicateDialogOpen = true"
            >
              {{ t('OUTBOUNDS.DUPLICATES.BANNER_ACTION') }}
            </button>
          </div>
        </div>
        <div
          v-if="dispatchResult"
          class="px-3 py-2 mb-3 text-sm rounded-lg bg-n-amber-2 text-n-amber-11"
        >
          <p class="font-medium">{{ t('OUTBOUNDS.DISPATCH.RESULT_TITLE') }}</p>
          <p class="mt-0.5">
            {{
              dispatchResult.count == null
                ? t('OUTBOUNDS.DISPATCH.RESULT_DETAIL_UNKNOWN')
                : t('OUTBOUNDS.DISPATCH.RESULT_DETAIL', {
                    count: dispatchResult.count,
                  })
            }}
          </p>
        </div>

        <!-- O que o contato vai LER, com um contato de verdade da lista — e não `Oi, {{1}}!`.
             Os avisos vêm do backend, que sabe coisas que a tela não tem como saber: variável
             que fica vazia, e template de marketing que a Meta pode reter. -->
        <div
          v-if="previa"
          class="p-4 mb-3 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <p class="mb-2 text-xs font-medium text-n-slate-11">
            {{ t('OUTBOUNDS.PREVIEW.TITLE', { nome: previa.to || '' }) }}
          </p>
          <p class="text-sm whitespace-pre-line text-n-slate-12">
            {{ previa.body }}
          </p>

          <dl
            v-if="previa.variables?.length"
            class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 mt-3 pt-3 border-t border-n-weak text-xs"
          >
            <template v-for="v in previa.variables" :key="v.name">
              <dt class="font-mono text-n-slate-11">{{ marcador(v.name) }}</dt>
              <dd
                class="font-medium"
                :class="v.is_fallback ? 'text-n-amber-11' : 'text-n-slate-12'"
              >
                {{ v.resolved || t('OUTBOUNDS.PREVIEW.EMPTY') }}
              </dd>
            </template>
          </dl>

          <div
            v-for="aviso in previa.warnings || []"
            :key="aviso.code"
            class="flex items-start gap-2 mt-2 text-xs text-n-amber-11"
          >
            <i class="i-lucide-alert-triangle size-3.5 mt-0.5 shrink-0" />
            <span>{{ aviso.message }}</span>
          </div>
        </div>
        <p v-else-if="previaErro" class="mb-3 text-xs text-n-slate-10">
          {{ previaErro }}
        </p>

        <div class="flex flex-col gap-4">
          <!-- Nome: sugerido a partir da audiência e da data, e editável. Sem ele o backend usa
               o nome da audiência como título, que é de onde vinham cinco campanhas idênticas. -->
          <div class="flex flex-col gap-1">
            <label
              for="nome-da-campanha"
              class="text-xs font-medium text-n-slate-12"
            >
              {{ t('OUTBOUNDS.DISPATCH.NAME_LABEL') }}
            </label>
            <input
              id="nome-da-campanha"
              v-model="nomeDaCampanha"
              type="text"
              :placeholder="nomeSugerido"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
            />
          </div>

          <!-- Agora x agendar. O agendamento reusa o cron que já colhe campanha `active` com
               hora vencida; a diferença de verdade é que a agendada pode ser cancelada. -->
          <div class="flex flex-col gap-2">
            <button
              type="button"
              class="flex items-start w-full gap-2 px-3 py-2 text-left border rounded-lg"
              :class="
                modoDeEnvio === MODO_AGORA
                  ? 'border-n-brand bg-n-alpha-2'
                  : 'border-n-weak'
              "
              @click="modoDeEnvio = MODO_AGORA"
            >
              <i
                class="size-4 mt-0.5 shrink-0"
                :class="
                  modoDeEnvio === MODO_AGORA
                    ? 'i-lucide-circle-dot text-n-brand'
                    : 'i-lucide-circle text-n-slate-10'
                "
              />
              <span>
                <span class="block text-sm font-medium text-n-slate-12">
                  {{ t('OUTBOUNDS.DISPATCH.SEND_NOW') }}
                </span>
                <span class="block text-xs text-n-slate-10">
                  {{ t('OUTBOUNDS.DISPATCH.SEND_NOW_HINT') }}
                </span>
              </span>
            </button>

            <button
              type="button"
              class="flex items-start w-full gap-2 px-3 py-2 text-left border rounded-lg"
              :class="
                modoDeEnvio === MODO_AGENDAR
                  ? 'border-n-brand bg-n-alpha-2'
                  : 'border-n-weak'
              "
              @click="modoDeEnvio = MODO_AGENDAR"
            >
              <i
                class="size-4 mt-0.5 shrink-0"
                :class="
                  modoDeEnvio === MODO_AGENDAR
                    ? 'i-lucide-circle-dot text-n-brand'
                    : 'i-lucide-circle text-n-slate-10'
                "
              />
              <span class="min-w-0">
                <span class="block text-sm font-medium text-n-slate-12">
                  {{ t('OUTBOUNDS.DISPATCH.SCHEDULE') }}
                </span>
                <span class="block text-xs text-n-slate-10">
                  {{ t('OUTBOUNDS.DISPATCH.SCHEDULE_HINT') }}
                </span>
              </span>
            </button>

            <input
              v-if="modoDeEnvio === MODO_AGENDAR"
              v-model="dataAgendada"
              type="datetime-local"
              :min="agoraLocal"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
            />
          </div>

          <dl class="text-xs text-n-slate-11 space-y-0.5">
            <div class="flex gap-2">
              <dt class="w-24 shrink-0">
                {{ t('OUTBOUNDS.DISPATCH.TEMPLATE') }}
              </dt>
              <dd class="font-medium break-all text-n-slate-12">
                {{
                  selectedTemplateName || t('OUTBOUNDS.DISPATCH.EMPTY_FIELD')
                }}
              </dd>
            </div>
            <div class="flex gap-2">
              <dt class="w-24 shrink-0">
                {{ t('OUTBOUNDS.DISPATCH.SEGMENT') }}
              </dt>
              <dd class="text-n-slate-12">
                {{ segmentLabel || t('OUTBOUNDS.DISPATCH.EMPTY_FIELD') }}
              </dd>
            </div>
            <div class="flex gap-2">
              <dt class="w-24 shrink-0">
                {{ t('OUTBOUNDS.DISPATCH.RECIPIENTS') }}
              </dt>
              <dd class="font-medium tabular-nums text-n-slate-12">
                {{ sendableContacts.length }}
              </dd>
            </div>
          </dl>

          <Button
            class="w-full"
            :disabled="
              !readyToDispatch || loading.sending || agendamentoInvalido
            "
            @click="dispatchDialogOpen = true"
          >
            <i
              class="mr-1 size-4"
              :class="
                modoDeEnvio === MODO_AGENDAR
                  ? 'i-lucide-calendar-clock'
                  : 'i-lucide-send'
              "
            />
            {{
              modoDeEnvio === MODO_AGENDAR
                ? t('OUTBOUNDS.DISPATCH.BUTTON_SCHEDULE')
                : t('OUTBOUNDS.DISPATCH.BUTTON')
            }}
          </Button>
        </div>
      </StepSection>

      <ContactDialog
        v-model:open="contactDialogOpen"
        :contact="editingContact"
        @save="saveContact"
      />

      <DispatchDialog
        v-model:open="dispatchDialogOpen"
        :agent-name="selectedChatbot?.name || ''"
        :template-name="selectedTemplateName"
        :preview-image-url="selectedTemplate?.preview_image_url || ''"
        :segment-name="segmentLabel"
        :sender-phone="integration?.sender_phone || ''"
        :count="sendableContacts.length"
        :opted-out-count="bloqueados.length"
        :opted-out-rows="linhasBloqueadas"
        :sending="loading.sending"
        @confirm="dispatch()"
      />

      <DuplicateAlertDialog
        v-model:open="duplicateDialogOpen"
        :duplicates="duplicates"
        :total-contacts="sendableContacts.length - linhasBloqueadas"
        :sending="loading.sending"
        @remove="removeDuplicates"
        @force="dispatch({ force: true })"
      />
    </div>

    <HistoryPeople v-else />
  </div>
</template>
